import React, { useState } from 'react';
import { 
  CheckCircle, 
  XCircle, 
  UserCheck, 
  Music, 
  MapPin, 
  Building2, 
  ShieldAlert, 
  AlertCircle,
  Eye,
  Flame
} from 'lucide-react';
import CyberBadge from '../common/CyberBadge';

export default function ApprovalsView({ 
  captains = [], 
  artists = [], 
  rooms = [], 
  onApproveCaptain, 
  onRejectCaptain, 
  onApproveArtist, 
  onRejectArtist,
  onApproveRoom,
  onRejectRoom,
  onSelectDrawer
}) {
  const [activeSubTab, setActiveSubTab] = useState('parties'); // 'parties', 'listings', 'captains', 'artists'

  // Filter pending items
  const pendingParties = rooms.filter(r => (r.categoryType === 'wormhole_party' || r.categoryType === 'party' || r.moduleData?.isWormhole) && r.status === 'pending');
  const pendingCaptains = captains.filter(c => c.status === 'pending');
  const pendingArtists = artists.filter(a => !a.verified && a.status !== 'rejected');
  const pendingRooms = rooms.filter(r => r.status === 'pending');

  return (
    <div className="animate-fade" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      
      {/* Header */}
      <div>
        <h1 style={{ fontSize: '1.35rem', fontWeight: 900, color: 'var(--text-primary)', letterSpacing: '-0.02em', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <CheckCircle size={22} style={{ color: 'var(--neon-blue)' }} /> Central Approvals & Moderation Queue
        </h1>
        <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: '2px' }}>
          Approve or reject pending Warm Home Parties, Captain accounts, Creator verifications, and Venue/Event room listings across the RapMap universe.
        </p>
      </div>

      {/* Sub tabs selectors */}
      <div style={{ display: 'flex', gap: '6px', borderBottom: '1px solid var(--border-color)', paddingBottom: '8px', flexWrap: 'wrap' }}>
        {[
          { id: 'parties', label: `Pending Parties (${pendingParties.length})`, color: 'var(--neon-purple)' },
          { id: 'listings', label: `Pending Property & Turfs (${pendingRooms.length})`, color: 'var(--neon-blue)' },
          { id: 'captains', label: `Pending Captains (${pendingCaptains.length})`, color: 'var(--neon-red)' },
          { id: 'artists', label: `Pending Artists (${pendingArtists.length})`, color: 'var(--neon-purple)' },
        ].map(tab => {
          const isActive = activeSubTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id)}
              style={{
                background: isActive ? 'linear-gradient(135deg, rgba(255, 45, 85, 0.16), rgba(139, 92, 246, 0.18))' : 'rgba(20, 16, 36, 0.5)',
                border: isActive ? `1px solid ${tab.color}` : '1px solid rgba(255,255,255,0.1)',
                color: isActive ? '#FFFFFF' : 'var(--text-muted)',
                padding: '5px 12px',
                borderRadius: 'var(--radius-full)',
                fontSize: '0.78rem',
                fontWeight: isActive ? 800 : 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: isActive ? `0 2px 10px ${tab.color}25` : 'none',
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* WARM HOME PARTIES PENDING SUB-TAB */}
      {activeSubTab === 'parties' && (
        <div className="cyber-card" style={{ padding: '14px 16px', borderColor: 'var(--border-purple)' }}>
          <h3 style={{ fontSize: '0.98rem', fontWeight: 800, color: '#FFFFFF', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Flame size={16} style={{ color: 'var(--neon-red)' }} /> Pending Warm Home & Wormhole Parties ({pendingParties.length})
          </h3>

          {pendingParties.length === 0 ? (
            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.82rem' }}>
              No pending Warm Home Parties waiting for moderation. Admin-created parties are auto-published directly.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '10px' }}>
              {pendingParties.map(party => (
                <div
                  key={party._id || party.id}
                  style={{
                    background: 'rgba(22, 16, 36, 0.70)',
                    border: '1px solid var(--border-color)',
                    borderTop: '1px solid var(--border-highlight)',
                    borderRadius: 'var(--radius-md)',
                    padding: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                  }}
                >
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <img
                      src={party.images?.[0] || 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&w=200&q=80'}
                      alt={party.title}
                      style={{ width: '64px', height: '64px', borderRadius: '12px', objectFit: 'cover', border: '1.5px solid #8B5CF6' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                        <span style={{ fontSize: '1.05rem', fontWeight: 900, color: '#FFFFFF' }} className="text-truncate">
                          {party.title}
                        </span>
                        <CyberBadge label="WORMHOLE PARTY" color="purple" />
                      </div>
                      <div style={{ fontSize: '0.78rem', color: '#A78BFA', fontWeight: 800, marginTop: '4px' }}>
                        ₹{party.price} / {party.priceType || 'ticket'}
                      </div>
                      <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <MapPin size={11} color="var(--neon-pink)" /> {party.city || party.location?.city || 'Indore'}
                      </div>
                    </div>
                  </div>

                  {party.description && (
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', background: 'rgba(255,255,255,0.03)', padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.08)' }}>
                      "{party.description}"
                    </div>
                  )}

                  <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', gap: '8px', marginTop: 'auto' }}>
                    <button className="btn btn-outline" style={{ fontSize: '0.74rem', padding: '8px' }} onClick={() => onSelectDrawer(party, 'room')}>
                      <Eye size={12} /> Details
                    </button>
                    <button className="btn btn-purple" style={{ fontSize: '0.74rem', padding: '8px', fontWeight: 800 }} onClick={() => onApproveRoom(party._id || party.id)}>
                      Approve Live
                    </button>
                    <button className="btn btn-danger" style={{ fontSize: '0.74rem', padding: '8px', fontWeight: 800 }} onClick={() => onRejectRoom(party._id || party.id)}>
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* LISTINGS PENDING SUB-TAB */}
      {activeSubTab === 'listings' && (
        <div className="cyber-card cyber-card-glow-cyan" style={{ padding: '24px' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: '#FFFFFF', marginBottom: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Building2 size={18} style={{ color: 'var(--neon-cyan)' }} /> Room, Event & Venue Submissions ({pendingRooms.length})
          </h3>

          {pendingRooms.length === 0 ? (
            <div style={{ padding: '36px', textAlign: 'center', color: 'var(--text-muted)' }}>
              No pending room or event listings waiting for moderation.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(365px, 1fr))', gap: '20px' }}>
              {pendingRooms.map(room => (
                <div
                  key={room._id || room.id}
                  style={{
                    background: 'rgba(9, 10, 17, 0.6)',
                    border: '1px solid rgba(0, 240, 255, 0.25)',
                    borderRadius: 'var(--radius-md)',
                    padding: '20px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '14px',
                  }}
                >
                  <div style={{ display: 'flex', gap: '14px' }}>
                    <img
                      src={room.images?.[0] || 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?auto=format&fit=crop&w=200&q=80'}
                      alt={room.title}
                      style={{ width: '64px', height: '64px', borderRadius: '12px', objectFit: 'cover', border: '1.5px solid var(--neon-cyan)' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                        <span style={{ fontSize: '1.05rem', fontWeight: 900, color: '#FFFFFF' }} className="text-truncate">
                          {room.title}
                        </span>
                        <CyberBadge label={(room.categoryType || room.moduleType || 'event').toUpperCase()} color="cyan" />
                      </div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--neon-cyan)', fontWeight: 800, marginTop: '4px' }}>
                        ₹{room.price} / {room.priceType || 'ticket'}
                      </div>
                      <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <MapPin size={11} color="var(--neon-pink)" /> {room.city || 'Indore'}
                      </div>
                    </div>
                  </div>

                  {room.description && (
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', background: 'rgba(255,255,255,0.03)', padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.08)' }}>
                      {room.description.length > 120 ? `${room.description.slice(0, 120)}...` : room.description}
                    </div>
                  )}

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    <span>Hosted By: </span>
                    <span style={{ color: '#FFFFFF', fontWeight: 800 }}>
                      {room.captain?.businessName || room.captain?.name || 'Captain Partner'}
                    </span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', gap: '8px', marginTop: 'auto' }}>
                    <button className="btn btn-outline" style={{ fontSize: '0.74rem', padding: '8px' }} onClick={() => onSelectDrawer(room, 'room')}>
                      <Eye size={12} /> View Details
                    </button>
                    <button className="btn btn-emerald" style={{ fontSize: '0.74rem', padding: '8px', fontWeight: 800 }} onClick={() => onApproveRoom(room._id || room.id)}>
                      Approve
                    </button>
                    <button className="btn btn-danger" style={{ fontSize: '0.74rem', padding: '8px', fontWeight: 800 }} onClick={() => onRejectRoom(room._id || room.id)}>
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* CAPTAINS PENDING SUB-TAB */}
      {activeSubTab === 'captains' && (
        <div className="cyber-card cyber-card-glow-purple" style={{ padding: '24px' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: '#FFFFFF', marginBottom: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <UserCheck size={18} style={{ color: 'var(--neon-purple)' }} /> Pending Captain Registration Applications ({pendingCaptains.length})
          </h3>

          {pendingCaptains.length === 0 ? (
            <div style={{ padding: '36px', textAlign: 'center', color: 'var(--text-muted)' }}>
              No pending Captain signups waiting for review.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(365px, 1fr))', gap: '20px' }}>
              {pendingCaptains.map(captain => (
                <div
                  key={captain._id || captain.id}
                  style={{
                    background: 'rgba(9, 10, 17, 0.6)',
                    border: '1px solid rgba(168, 85, 247, 0.25)',
                    borderRadius: 'var(--radius-md)',
                    padding: '20px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '14px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: 'rgba(168, 85, 247, 0.15)', borderWidth: '1px', borderColor: 'var(--neon-purple)', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '1.1rem', fontWeight: 900, color: 'var(--neon-purple)' }}>
                      {(captain.name || 'C').slice(0,2).toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '1.05rem', fontWeight: 900, color: '#FFFFFF' }}>
                        {captain.name || 'Captain Partner'}
                      </div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                        {captain.email}
                      </div>
                      <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <MapPin size={11} color="var(--neon-pink)" /> {captain.city || 'Indore'}
                      </div>
                      {captain.documents && captain.documents.length > 0 && (
                        <div style={{ marginTop: '4px', fontSize: '0.72rem', color: 'var(--neon-cyan)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          📄 {captain.documents.length} Verification Document(s) Attached
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', gap: '8px', marginTop: 'auto' }}>
                    <button className="btn btn-outline" style={{ fontSize: '0.74rem', padding: '8px' }} onClick={() => onSelectDrawer(captain, 'captain')}>
                      <Eye size={12} /> View Details
                    </button>
                    <button className="btn btn-purple" style={{ fontSize: '0.74rem', padding: '8px', fontWeight: 800 }} onClick={() => onApproveCaptain(captain._id || captain.id)}>
                      Approve
                    </button>
                    <button className="btn btn-danger" style={{ fontSize: '0.74rem', padding: '8px', fontWeight: 800 }} onClick={() => onRejectCaptain(captain._id || captain.id)}>
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ARTISTS PENDING SUB-TAB */}
      {activeSubTab === 'artists' && (
        <div className="cyber-card cyber-card-glow-pink" style={{ padding: '24px' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: '#FFFFFF', marginBottom: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Music size={18} style={{ color: 'var(--neon-pink)' }} /> Creator Verification Requests ({pendingArtists.length})
          </h3>

          {pendingArtists.length === 0 ? (
            <div style={{ padding: '36px', textAlign: 'center', color: 'var(--text-muted)' }}>
              No pending creator pro verification requests.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(365px, 1fr))', gap: '20px' }}>
              {pendingArtists.map(artist => (
                <div
                  key={artist._id || artist.id}
                  style={{
                    background: 'rgba(9, 10, 17, 0.6)',
                    border: '1px solid rgba(246, 44, 101, 0.25)',
                    borderRadius: 'var(--radius-md)',
                    padding: '20px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '14px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <img
                      src={artist.profileImage || artist.avatar || 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?auto=format&fit=crop&w=200&q=80'}
                      alt={artist.stageName || artist.name}
                      style={{ width: '54px', height: '54px', borderRadius: '12px', objectFit: 'cover', border: '1.5px solid var(--neon-pink)' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '1.05rem', fontWeight: 900, color: '#FFFFFF' }}>
                        {artist.stageName || artist.name || 'MC Creator'}
                      </div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--neon-pink)', fontWeight: 800, marginTop: '2px' }}>
                        {artist.primarySkill || 'RAPPER'}
                      </div>
                      <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <MapPin size={11} color="var(--neon-pink)" /> {artist.city || 'Indore'}
                      </div>
                    </div>
                  </div>

                  {artist.bio && (
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', background: 'rgba(255,255,255,0.03)', padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.08)' }}>
                      "{artist.bio}"
                    </div>
                  )}

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: 'auto' }}>
                    <button className="btn btn-pink" style={{ fontSize: '0.74rem', padding: '8px', fontWeight: 800 }} onClick={() => onApproveArtist(artist._id || artist.id)}>
                      Approve & Verify
                    </button>
                    <button className="btn btn-danger" style={{ fontSize: '0.74rem', padding: '8px', fontWeight: 800 }} onClick={() => onRejectArtist(artist._id || artist.id)}>
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

    </div>
  );
}
